import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ContentFormFields from "./ContentFormFields";
import ContentUploadPanel from "./ContentUploadPanel";
import Button from "../Common/Button";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";

const ContentDetails = ({
    item,
    isEditing,
    onEdit,
    onCancel,
    onSave,
    onDelete,
    isDeleting = false,
    onMediaUploaded,
}) => {
    const [uploadedMedia, setUploadedMedia] = useState({
        mediaUrl: item?.fileUrl || "",
        cloudflareId: item?.cloudflareId || "",
        thumbnailUrl: item?.thumbnail || "",
        thumbnailCloudflareId: item?.thumbnailCloudflareId || "",
    });
    const [isUploading, setIsUploading] = useState(false);
    const { t } = useTranslation();

    const { control, handleSubmit, reset, formState: { errors, isValid } } = useForm({
        mode: "onChange",
        defaultValues: {
            title: item?.title || "",
            platform: item?.platform ? { value: item.platform, label: item.platform } : null,
            link: item?.link || "",
            fileType: item?.fileType ? { value: item.fileType, label: item.fileType } : null,
            postType: item?.postType ? { value: item.postType, label: item.postType } : null,
            caption: item?.caption || "",
            acceptedTerms: false,
        },
    });

    const watchedFileType = useWatch({ control, name: "fileType" });
    const selectedFileType = watchedFileType?.value || "";
    const isSaveDisabled = !isValid || isUploading || !uploadedMedia.mediaUrl;

    useEffect(() => {
        reset({
            title: item?.title || "",
            platform: item?.platform ? { value: item.platform, label: item.platform } : null,
            link: item?.link || "",
            fileType: item?.fileType ? { value: item.fileType, label: item.fileType } : null,
            postType: item?.postType ? { value: item.postType, label: item.postType } : null,
            caption: item?.caption || "",
            acceptedTerms: false,
        });
        setUploadedMedia({
            mediaUrl: item?.fileUrl || "",
            cloudflareId: item?.cloudflareId || "",
            thumbnailUrl: item?.thumbnail || "",
            thumbnailCloudflareId: item?.thumbnailCloudflareId || "",
        });
    }, [item, reset]);

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors, "Please fill in all required fields before saving.");
    };

    const handleSaveClick = handleSubmit((values) => {
        onSave?.({
            ...values,
            platform: values.platform?.value || "",
            fileType: values.fileType?.value || "",
            postType: values.postType?.value || "",
            ...uploadedMedia,
        });
    }, handleFormInvalid);

    return (
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="order-2 lg:order-1">
                {!isEditing && (
                    <ContentFormFields mode="view" data={item} />
                )}
                {isEditing && (
                    <form className="space-y-4" onSubmit={handleSaveClick}>
                        <ContentFormFields
                            mode="edit"
                            control={control}
                            errors={errors}
                            showTerms={false}
                            showTitle={false}
                        />
                    </form>
                )}
                {isEditing && (
                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <Button
                            type="button"
                            onClick={handleSaveClick}
                            disabled={isSaveDisabled}
                            className="w-full"
                        >
                            {t("CONTENT_BTN_SAVE_CHANGES")}
                        </Button>
                        <Button
                            type="button"
                            onClick={onCancel}
                            variant="secondary"
                            className="w-full"
                        >
                            {t("COMMON_CANCEL")}
                        </Button>
                    </div>
                )}
            </div>
            <div className="order-1 lg:order-2">
                <ContentUploadPanel
                    initialPreview={
                        item.fileType === "Video" && item.fileUrl
                            ? item.fileUrl
                            : item.preview
                    }
                    readOnly={!isEditing}
                    showUpload={isEditing}
                    selectedFileType={selectedFileType}
                    uploadEnabled={!!selectedFileType}
                    onUploadStart={() => setIsUploading(true)}
                    onClear={() => {
                        setUploadedMedia({ mediaUrl: "", cloudflareId: "", thumbnailUrl: "", thumbnailCloudflareId: "" });
                    }}
                    onUploadComplete={(result) => {
                        setIsUploading(false);
                        const prevMedia = uploadedMedia;
                        const mediaUrl = result.mediaUrl || "";
                        const cloudflareId = result.cloudflareId || "";
                        const isVideo = result.fileType === "Video";
                        const thumbnailUrl = isVideo && cloudflareId
                            ? `https://videodelivery.net/${cloudflareId}/thumbnails/thumbnail.jpg`
                            : mediaUrl;

                        setUploadedMedia({
                            mediaUrl,
                            cloudflareId,
                            thumbnailUrl,
                            thumbnailCloudflareId: isVideo ? cloudflareId : "",
                        });
                        onMediaUploaded?.(
                            {
                                mediaUrl,
                                cloudflareId,
                                thumbnailUrl,
                                thumbnailCloudflareId: isVideo ? cloudflareId : "",
                                fileType: result.fileType,
                            },
                            prevMedia
                        );
                    }}
                    onUploadError={() => setIsUploading(false)}
                />
            </div>
        </div>
    );
};

export default ContentDetails;
